/**
 * PATH: packages/email-hub/src/feature/components/usePingHandoff.ts
 * FILE_ID: EMAIL-COLLAB-COMPONENT-USE-PING-HANDOFF-001
 * PURPOSE: Detect whether the current user has a pending ping/handoff on one
 *          email — pure detection only.
 * WHY:     Change #269 (Notice Center unification) needs to know a ping
 *          exists BEFORE deciding to mount PingNotificationCard, so
 *          `buildEmailNotices` (a pure synchronous selector that must never
 *          fetch) can produce the `ping` notice. Extracted from
 *          PingNotificationCard's own effect, which previously fetched AND
 *          stamped "viewed" together on mount. Detection and the audit
 *          "viewed" stamp are now deliberately split: this hook only
 *          detects; PingNotificationCard's mount effect stamps viewed (see
 *          its file header) — the caller controls WHEN that component
 *          mounts, so "viewed" means "the operator opened the notice," never
 *          "the app noticed a ping exists."
 * FLOW:
 *   GET assignments (to me) -> match this email -> handoff | null
 * DEPENDENCIES:
 *   - ../lib/api (emailApi): GET assignments
 * RELATED:
 *   - PingNotificationCard.tsx: renders the detected handoff; stamps viewed.
 *   - reader-header/adapters/ReaderHeaderAdapter.tsx: flag-ON call site.
 *   - components/email-reader/ReaderWorkflowSections.tsx,
 *     components/email-preview-pane/PreviewReadingSurface.tsx: flag-OFF
 *     legacy call sites, migrated to this hook so no component self-fetches.
 * NOTES FOR FUTURE AI: Do not add the view-stamp POST here — see WHY above.
 * Passing an empty `emailId` is a valid "no email selected" no-op, not an
 * error, so hosts can call this unconditionally before any early return.
 */
import { useEffect, useState } from 'react'

import { emailApi } from '../lib/api'

/** Subset of the EmailHandoff serializer (assignment_views._serialize_assignment)
 *  that the ping surfaces actually read. */
export interface MyHandoff {
  id: string
  email_id: string
  assigned_by_name: string | null
  message: string
  assigned_at: string | null
  dismissed_at: string | null
}

/**
 * Detect the current user's pending ping/handoff on one email, if any.
 *
 * Inputs: the email id (empty string is a no-op — returns null without
 * fetching). Output: the matching handoff, or null when there is none (no
 * assignment, dismissed, or belongs to a different email/user). Side
 * effects: one GET per emailId change; no writes, ever. Failure modes: any
 * request error (including a 404 on backends without the assignment
 * surface) resolves to null rather than throwing.
 */
export function usePingHandoff(emailId: string): MyHandoff | null {
  const [handoff, setHandoff] = useState<MyHandoff | null>(null)

  useEffect(() => {
    let cancelled = false
    setHandoff(null)
    if (!emailId) return
    // "Assigned to me" — the endpoint scopes to request.user server-side, so
    // a match here means *I* was pinged on this email (the sender never
    // sees it).
    emailApi<{ results: MyHandoff[] }>('/api/emails/assignments/?status=pending')
      .then((data) => {
        if (cancelled) return
        const mine = (data?.results ?? []).find(
          (a) => a.email_id === emailId && !a.dismissed_at,
        )
        setHandoff(mine ?? null)
      })
      .catch(() => {
        /* no assignment surface on older backends — treat as no ping */
      })
    return () => {
      cancelled = true
    }
  }, [emailId])

  return handoff
}
